import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import { FUNCTION_URLS } from "../config/functionUrls";
import rutaManuales from "../data/rutaManuales";
import nombres from "../data/nombresPantalla.json";
import { saveMessages, saveFeedback, loadChatHistory } from "../services/vexaChatService";

const WELCOME_MSG = { sender: "axis", text: "¡Hola! Soy Axis Orquestador, un agente de Inteligencia Artificial creado para resolver tus dudas sobre Neurum OS. ¿En qué te puedo ayudar hoy?" };

function resolveCurrentContext(pathname) {
  // Exact match first
  if (rutaManuales[pathname]) {
    const entry = rutaManuales[pathname];
    const friendlyName = nombres[entry.archivo] || entry.archivo?.replace(/\.(js|jsx)$/i, "") || pathname;
    return { pathname, modulo: entry.modulo, archivo: entry.archivo, componente: friendlyName };
  }
  // Try matching with dynamic segments (e.g., /vacantes/123 → /vacantes/:id)
  for (const [route, entry] of Object.entries(rutaManuales)) {
    if (!route.includes(":")) continue;
    const pattern = new RegExp("^" + route.replace(/:[^/]+/g, "[^/]+") + "$");
    if (pattern.test(pathname)) {
      const friendlyName = nombres[entry.archivo] || entry.archivo?.replace(/\.(js|jsx)$/i, "") || pathname;
      return { pathname, modulo: entry.modulo, archivo: entry.archivo, componente: friendlyName };
    }
  }
  // Prefix match (e.g., /planillas/123/exportacion → matches /planillas/:planillaId/exportacion)
  for (const [route, entry] of Object.entries(rutaManuales)) {
    const routePrefix = route.replace(/:[^/]+/g, "").replace(/\/$/, "");
    if (routePrefix && pathname.startsWith(routePrefix)) {
      const friendlyName = nombres[entry.archivo] || entry.archivo?.replace(/\.(js|jsx)$/i, "") || pathname;
      return { pathname, modulo: entry.modulo, archivo: entry.archivo, componente: friendlyName };
    }
  }
  return { pathname, modulo: null, archivo: null, componente: null };
}

function normalizeModulo(name) {
  return name
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function convertRouteToLinks(text) {
  return text.replace(/(Vaya a\s+)(\*\*?)?([^*\n]+?)(\*\*?)?(?:\n|\.)/g, (match, prefix, bold1, route, bold2, suffix) => {
    const parts = route.split("→").map(s => s.trim());
    if (parts.length < 2) return match;
    const modulo = normalizeModulo(parts[0]);
    const componente = parts[1];
    // Look up the route in rutaManuales by normalized names
    for (const [path, entry] of Object.entries(rutaManuales)) {
      if (!entry.archivo) continue;
      const friendlyName = nombres[entry.archivo] || entry.archivo.replace(/\.(js|jsx)$/i, "");
      const entryModulo = normalizeModulo(entry.modulo || "");
      if (entryModulo === modulo && friendlyName === componente) {
        return `${prefix}[${bold1 || ""}${route}${bold2 || ""}](${path})${suffix}`;
      }
    }
    // Second pass: try modulo + componente contains
    for (const [path, entry] of Object.entries(rutaManuales)) {
      if (!entry.archivo) continue;
      const friendlyName = nombres[entry.archivo] || entry.archivo.replace(/\.(js|jsx)$/i, "");
      const entryModulo = normalizeModulo(entry.modulo || "");
      if (entryModulo === modulo && componente.includes(friendlyName)) {
        return `${prefix}[${bold1 || ""}${route}${bold2 || ""}](${path})${suffix}`;
      }
    }
    return match;
  });
}

function detectFallback(text) {
  if (!text) return false;
  const patterns = [
    "no encontré", "no encontre", "no se encuentra", 
    "no está disponible", "no está documentado", 
    "no tengo información", "no dispongo"
  ];
  return patterns.some(p => text.toLowerCase().includes(p));
}

const CONTEXTUAL_SUGGESTIONS = {
  GestionUsuarios: {
    Usuarios: ["¿Cómo creo un usuario?", "¿Cómo reseteo una contraseña?", "¿Qué permisos necesito para eliminar usuarios?"],
  },
  default: ["¿Cómo funciona este módulo?", "¿Qué puedo hacer aquí?", "¿Quién puede usar esta pantalla?"],
};

export default function AyudaBoton({ usuario }) {
  const location = useLocation();
  const navigate = useNavigate();

  const currentContext = resolveCurrentContext(location.pathname);
  const [showAxis, setShowAxis] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [question, setQuestion] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [showFallbackPrompt, setShowFallbackPrompt] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const messagesEndRef = useRef(null);
  const axisInputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setShowAxis(false);
    setShowFallbackPrompt(false);
  }, [location.pathname]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load chat history from backend
  useEffect(() => {
    if (usuario?.dni) {
      loadChatHistory(usuario.dni).then((history) => {
        if (history && history.length > 0) {
          setMessages(history);
          const fb = {};
          history.forEach((msg, idx) => {
            if (msg.sender === "axis" && msg.feedback) {
              fb[idx] = msg.feedback;
            }
          });
          setFeedback(fb);
        }
      });
    }
  }, [usuario?.dni]);

  const sendQuestion = async () => {
    if (!question.trim() || chatLoading) return;

    // Build context-enriched message
    let contextualQuestion = question;

    const userMsg = { sender: "user", text: question, moduloActual: currentContext.modulo, componenteActual: currentContext.componente, rutaActual: currentContext.pathname };
    setMessages(prev => [...prev, userMsg]);
    setQuestion("");
    setChatLoading(true);
    setShowFallbackPrompt(false);
    try {
      const response = await axios.post(FUNCTION_URLS.axisOrquestadorLegacy, {
        From: usuario?.dni || "WEB_USER",
        Body: contextualQuestion,
        RUC: usuario?.ruc || usuario?.rucEmpresa,
        rerank: true,
        mode: "consulta_manuales",
        moduloActual: currentContext.modulo,
        componenteActual: currentContext.componente,
        rutaActual: currentContext.pathname,
      });
      const data = response.data;
      let rawText = "No recibí una respuesta clara.";

      if (data && typeof data === 'object' && data.answer !== undefined) {
        rawText = data.answer || rawText;
      }
      if (typeof rawText === 'object') rawText = String(rawText);

      const lines = rawText.split('\n');
      const suggestions = lines
        .filter(line => line.trim().startsWith('🔹'))
        .map(line => line.replace(/^🔹\s*/, '').trim());
      const mainText = lines.filter(line => !line.trim().startsWith('🔹')).join('\n').trim();

      const textWithLinks = convertRouteToLinks(mainText);
      const isFallback = detectFallback(textWithLinks);
      const axisMsg = { sender: "axis", text: textWithLinks, suggestions, userQuestion: question, isFallback };

      setMessages(prev => [...prev, axisMsg]);

      if (isFallback) {
        setShowFallbackPrompt(true);
      }
    } catch (error) {
      setMessages(prev => [...prev, { sender: "axis", text: `Lo siento, ocurrió un error al procesar tu consulta: ${error.message}`, isError: true }]);
    } finally {
      setChatLoading(false);
      axisInputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  const navigateToComponent = (ruta) => {
    setShowAxis(false);
    navigate(ruta);
  };

  return (
    <>
      <div style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 1050,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "10px",
      }}>
        <button
          onClick={() => { setShowAxis(!showAxis); setShowTooltip(false); }}
          title="Axis Chat"
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "#0d6efd",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(13,110,253,0.4)",
            transition: "transform 0.2s, box-shadow 0.2s",
            fontSize: "26px",
            padding: 0,
            overflow: "hidden",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(13,110,253,0.6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(13,110,253,0.4)";
          }}
        >
          {showAxis ? (
            <FaTimes style={{ fontSize: "24px" }} />
          ) : (
            <img
              src="/images/axis.jpg"
              alt="Axis"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}
        </button>
        {showTooltip && !showAxis && (
          <div onClick={() => { setShowAxis(true); setShowTooltip(false); }} style={{
            background: "#fff",
            borderRadius: "14px 14px 14px 4px",
            padding: "10px 14px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
            maxWidth: "200px",
            fontSize: "13px",
            color: "#111827",
            lineHeight: 1.4,
            cursor: "pointer",
            position: "relative",
            marginBottom: "4px",
            animation: "axisBounce 2s ease-in-out infinite",
            border: "1px solid #e5e7eb",
          }}>
            🤖 ¡Chatea conmigo! Soy Axis, tu asistente de soporte técnico
            <div style={{
              position: "absolute",
              bottom: "-8px",
              right: "16px",
              width: "12px",
              height: "12px",
              background: "#fff",
              borderRight: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              transform: "rotate(45deg)",
            }} />
          </div>
        )}
      </div>
      <div style={{
        position: "fixed",
        right: "90px",
        bottom: "90px",
        width: "460px",
        height: "620px",
        background: "#fff",
        borderRadius: "16px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        display: showAxis ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 1060,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        <div style={{
          background: "linear-gradient(90deg, #1e3158, #2d4f9a)",
          color: "#fff",
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexShrink: 0,
        }}>
          <div style={{
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <img
              src="/images/axis.jpg"
              alt="Axis"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "15px" }}>Axis Orquestador (Beta)</div>
            <div style={{ fontSize: "11px", opacity: 0.7 }}>Especialista en Soporte Técnico</div>
          </div>
          <button
            onClick={() => setShowAxis(false)}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: "18px",
              opacity: 0.7,
              padding: "4px",
            }}
          >
            <FaTimes />
          </button>
        </div>
        {currentContext.modulo && currentContext.componente && (
          <div style={{
            background: "#e8f0fe",
            padding: "8px 14px",
            fontSize: "12px",
            color: "#1a56db",
            borderBottom: "1px solid #c5d9f7",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}>
            <span>📍</span>
            <span>{currentContext.modulo} → {currentContext.componente}</span>
          </div>
        )}

        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          background: "#f5f6f8",
        }}>
          {messages.map((msg, idx) => (
            <div key={idx}>
              {msg.sender === "user" ? (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{
                    maxWidth: "80%",
                    background: "#0d6efd",
                    color: "#fff",
                    borderRadius: "18px 18px 4px 18px",
                    padding: "10px 16px",
                    fontSize: "14px",
                    lineHeight: 1.4,
                    wordWrap: "break-word",
                  }}>
                    {msg.text}
                  </div>
                </div>
              ) : (
                <div>
                  <div 
                    onClick={(e) => {
                      const target = e.target.closest("a");
                      if (target) {
                        const href = target.getAttribute("href");
                        if (href && href.startsWith("/")) {
                          e.preventDefault();
                          navigateToComponent(href);
                        }
                      }
                    }}
                    style={{
                      maxWidth: "90%",
                      background: "#fff",
                      color: "#212529",
                      borderRadius: "18px 18px 18px 4px",
                      padding: "12px 16px",
                      fontSize: "14px",
                      lineHeight: 1.5,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                      wordWrap: "break-word",
                    }}>
                    {msg.isError ? (
                      <span style={{ color: "#dc3545" }}>{msg.text}</span>
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <span style={{ display: "block", marginBottom: "0.4rem", lineHeight: 1.5 }}>{children}</span>,
                          strong: ({ children }) => <strong style={{ color: "#111" }}>{children}</strong>,
                          ul: ({ children }) => <ul style={{ paddingLeft: "1.2rem", marginBottom: "0.3rem" }}>{children}</ul>,
                          ol: ({ children }) => <ol className="axis-ordered-list" style={{ paddingLeft: "1.8rem", marginBottom: "0.5rem", listStyleType: "decimal", display: "block" }}>{children}</ol>,
                          li: ({ children }) => <li className="axis-list-item" style={{ marginBottom: "0.2rem", display: "list-item" }}>{children}</li>,
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    )}
                  </div>
                  {msg.sender !== "user" && !msg.isError && (
                    <div style={{ display: "flex", gap: "6px", marginTop: "6px", justifyContent: "flex-start" }}>
                      <button
                        onClick={() => {
                          const key = idx;
                          const newVal = feedback[key] === "up" ? null : "up";
                          setFeedback(prev => ({ ...prev, [key]: newVal }));
                          if (usuario?.dni) saveFeedback(usuario.dni, key, newVal);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "14px",
                          padding: "2px 6px",
                          opacity: feedback[idx] === "up" ? 1 : 0.35,
                          transition: "opacity 0.15s",
                        }}
                        title="Útil"
                      >
                        👍
                      </button>
                      <button
                        onClick={() => {
                          const key = idx;
                          const newVal = feedback[key] === "down" ? null : "down";
                          setFeedback(prev => ({ ...prev, [key]: newVal }));
                          if (usuario?.dni) saveFeedback(usuario.dni, key, newVal);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "14px",
                          padding: "2px 6px",
                          opacity: feedback[idx] === "down" ? 1 : 0.35,
                          transition: "opacity 0.15s",
                        }}
                        title="No útil"
                      >
                        👎
                      </button>
                    </div>
                  )}
                  {msg.suggestions && msg.suggestions.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      {msg.suggestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            setQuestion(q);
                            setTimeout(() => sendQuestion(), 100);
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            padding: "8px 12px",
                            marginBottom: "4px",
                            border: "1px solid rgba(13,110,253,0.25)",
                            borderRadius: "8px",
                            background: "rgba(13,110,253,0.06)",
                            color: "#1a56db",
                            cursor: "pointer",
                            fontSize: "13px",
                            lineHeight: 1.4,
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(13,110,253,0.12)"}
                          onMouseLeave={e => e.currentTarget.style.background = "rgba(13,110,253,0.06)"}
                        >
                          💬 {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )          )}
          {showFallbackPrompt && (
            <div style={{
              background: "#fff8e1",
              border: "1px solid #ffe082",
              borderRadius: "12px",
              padding: "12px 14px",
              fontSize: "13px",
              color: "#6d4c00",
              lineHeight: 1.5,
            }}>
              <strong>💡 No encontré esa información.</strong>
              <div style={{ marginTop: "6px" }}>
                ¿Podría indicarme el módulo o la pantalla donde está trabajando? Así puedo buscar con más precisión.
              </div>
              <div style={{ marginTop: "8px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {currentContext.modulo && currentContext.componente && (
                  <button
                    onClick={() => {
                      setQuestion(`Estoy en ${currentContext.modulo} → ${currentContext.componente}. ${question.length > 0 ? question : "¿Cómo funciona esta pantalla?"}`);
                      axisInputRef.current?.focus();
                    }}
                    style={{
                      background: "#fff3e0",
                      border: "1px solid #ffcc80",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      color: "#e65100",
                    }}
                  >
                    Ya estoy en {currentContext.componente}
                  </button>
                )}
                <button
                  onClick={() => {
                    setQuestion("¿Cuáles son los módulos disponibles?");
                    setTimeout(() => sendQuestion(), 100);
                  }}
                  style={{
                    background: "#e8f5e9",
                    border: "1px solid #a5d6a7",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: "12px",
                    color: "#2e7d32",
                  }}
                >
                  Ver módulos disponibles
                </button>
              </div>
            </div>
          )}
          {chatLoading && (
            <div style={{ display: "flex", gap: "6px", padding: "8px 0" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#9ca3af", animation: "axisDotAnim 1.4s infinite ease-in-out" }} />
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#9ca3af", animation: "axisDotAnim 1.4s infinite ease-in-out", animationDelay: "0.2s" }} />
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#9ca3af", animation: "axisDotAnim 1.4s infinite ease-in-out", animationDelay: "0.4s" }} />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div style={{
          padding: "12px 14px",
          borderTop: "1px solid #e5e7eb",
          background: "#fff",
          flexShrink: 0,
        }}>
          <form
            onSubmit={(e) => { e.preventDefault(); sendQuestion(); }}
            style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}
          >
            <textarea
              ref={axisInputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={chatLoading}
              placeholder="Escribe tu mensaje..."
              rows={1}
              style={{
                flex: 1,
                border: "1px solid #d0d8e3",
                borderRadius: "24px",
                padding: "10px 16px",
                fontSize: "14px",
                resize: "none",
                outline: "none",
                fontFamily: "inherit",
                background: "#f8f9fb",
                color: "#111827",
                maxHeight: "100px",
                lineHeight: 1.4,
              }}
            />
            <button
              type="submit"
              disabled={chatLoading || !question.trim()}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "none",
                background: (chatLoading || !question.trim()) ? "rgba(0, 0, 0, 0.05)" : "#0d6efd",
                color: (chatLoading || !question.trim()) ? "#a0aec0" : "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: (chatLoading || !question.trim()) ? "not-allowed" : "pointer",
                flexShrink: 0,
                transition: "all 0.15s",
              }}
            >
              <span style={{
                fontSize: "16px",
                display: "inline-block",
                transform: "translate(1px, 0px)",
                lineHeight: 1
              }}>➤</span>
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes axisDotAnim {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
        @keyframes axisBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}
